import React, { useState, useEffect, useMemo } from 'react';
import { ReportData } from '../../types/reportTypes';
import MarketingReport from '../MarketingReport';
import ReportWithPDFExport, { BrandingTheme, PDFLayout, PageSize } from '../ReportWithPDFExport';
import MobileOptimizedReportView from './MobileOptimizedReportView';
import PaginatedReportView from './PaginatedReportView';
import { useScreenSize, useTouchCapability } from './MobileReportInteractions';
import { FilterState } from '../../types/filterTypes';

// Export these types so they can be imported from this file
export { BrandingTheme, PDFLayout, PageSize };

// Define the view modes available
export enum ViewMode {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE_PORTRAIT = 'mobile-portrait',
  MOBILE_LANDSCAPE = 'mobile-landscape'
}

interface OrientationAdapterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Adapts layout based on device orientation
 * Provides appropriate spacing and sizing for different orientations
 */
export const OrientationAdapter: React.FC<OrientationAdapterProps> = ({ children, className = '' }) => {
  const { isPortrait, width, height } = useScreenSize();
  
  // Get orientation-specific classes
  const orientationClass = isPortrait
    ? 'orientation-portrait'
    : 'orientation-landscape';
  
  // Apply padding adjustments based on orientation
  const paddingClass = isPortrait
    ? 'px-4 py-6'
    : 'px-6 py-3';
  
  // Apply different max-height constraints based on orientation
  const heightClass = isPortrait
    ? 'max-h-[90vh]'
    : 'max-h-[85vh]';
    
  return (
    <div className={`${orientationClass} ${paddingClass} ${heightClass} ${className}`}>
      {children}
    </div>
  );
};

interface ResponsiveReportViewProps {
  data: ReportData;
  reportId?: string;
  filterState?: FilterState;
  usePagination?: boolean;
  pdfOptions?: {
    layout?: PDFLayout;
    pageSize?: PageSize;
    brandingTheme?: BrandingTheme;
    includeHeader?: boolean;
    includeFooter?: boolean;
    companyName?: string;
    documentTitle?: string;
  };
}

/**
 * Enhanced Responsive Report View that adapts to screen size, device capabilities,
 * and orientation changes. Provides the appropriate view based on the user's device:
 * - Desktop: Standard or Paginated view based on data size
 * - Tablet: Optimized tablet view
 * - Mobile Portrait: Vertically optimized touch view with swipe gestures
 * - Mobile Landscape: Horizontally optimized view with adapted controls
 */
const ResponsiveReportView: React.FC<ResponsiveReportViewProps> = ({
  data,
  reportId,
  filterState,
  usePagination = false,
  pdfOptions = {}
}) => {
  // Get device capabilities and screen information
  const { isPortrait, width, height, isMobile } = useScreenSize();
  const isTouchDevice = useTouchCapability();
  
  // Calculate whether this is a large report that needs pagination
  const isLargeReport = usePagination || (data?.jobData?.length || 0) > 20;
  
  // Determine the current view mode based on device and orientation
  const viewMode = useMemo(() => {
    if (!isMobile && width >= 1024) return ViewMode.DESKTOP;
    if (!isMobile && width >= 768) return ViewMode.TABLET;
    if (isMobile && isPortrait) return ViewMode.MOBILE_PORTRAIT;
    if (isMobile && !isPortrait) return ViewMode.MOBILE_LANDSCAPE;
    return ViewMode.DESKTOP; // Default fallback
  }, [isMobile, isPortrait, width]);
  
  // Listen for orientation changes and apply adaptations
  useEffect(() => {
    // Set CSS custom properties for orientation-specific values
    document.documentElement.style.setProperty(
      '--touch-target-size', 
      isPortrait ? '48px' : '56px'
    );
    
    document.documentElement.style.setProperty(
      '--landscape-control-spacing', 
      isPortrait ? '16px' : '8px'
    );
    
    // Add orientation class to body for global styling
    if (isPortrait) {
      document.body.classList.remove('landscape-mode');
      document.body.classList.add('portrait-mode');
    } else {
      document.body.classList.remove('portrait-mode');
      document.body.classList.add('landscape-mode');
    }
    
    return () => {
      // Clean up orientation classes
      document.body.classList.remove('portrait-mode', 'landscape-mode');
    };
  }, [isPortrait]);
  
  // Create a component that wraps content based on the view mode
  const ViewWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (viewMode === ViewMode.MOBILE_LANDSCAPE) {
      return (
        <div className="landscape-optimized-container overflow-x-auto">
          {/* For landscape, use a row layout with horizontal scrolling */}
          <div className="landscape-content-wrapper flex flex-row items-start gap-3 pb-4">
            {children}
          </div>
          {/* Add landscape-specific bottom navigation */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-1 flex justify-around">
            <button className="p-2 rounded-full bg-gray-100">
              <span className="sr-only">Previous</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button className="p-2 rounded-full bg-gray-100">
              <span className="sr-only">Home</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <button className="p-2 rounded-full bg-gray-100">
              <span className="sr-only">Next</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button className="p-2 rounded-full bg-gray-100">
              <span className="sr-only">Export</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </button>
          </div>
        </div>
      );
    }
    
    return <OrientationAdapter>{children}</OrientationAdapter>;
  };
  
  // Determine which view to show based on device, orientation and data size
  const renderReportView = () => {
    // For mobile devices, use the mobile-optimized view with orientation adaptation
    if (viewMode === ViewMode.MOBILE_PORTRAIT || viewMode === ViewMode.MOBILE_LANDSCAPE) {
      return (
        <MobileOptimizedReportView 
          data={data}
          onExportPDF={() => {
            // If using the PDF export component, trigger its export function
            if (pdfExportRef.current) {
              const exportButton = pdfExportRef.current.querySelector('button');
              if (exportButton) exportButton.click();
            }
          }}
        />
      );
    }
    
    // For tablet devices, provide an intermediate experience
    if (viewMode === ViewMode.TABLET) {
      // Use the standard report, but with some mobile optimizations if it's a touch device
      return (
        <div className="tablet-optimized-view">
          {isTouchDevice ? (
            <MobileOptimizedReportView 
              data={data}
              onExportPDF={() => {
                if (pdfExportRef.current) {
                  const exportButton = pdfExportRef.current.querySelector('button');
                  if (exportButton) exportButton.click();
                }
              }}
            />
          ) : (
            <MarketingReport data={data} />
          )}
        </div>
      );
    }
    
    // For desktop with large reports, use the paginated view
    if (isLargeReport && reportId && filterState) {
      return (
        <PaginatedReportView
          reportId={reportId}
          filterState={filterState}
          initialData={data}
        />
      );
    }
    
    // For regular desktop view, use the standard report
    return <MarketingReport data={data} />;
  };
  
  // Create a ref to the PDF export component to trigger its export function
  const pdfExportRef = React.useRef<HTMLDivElement>(null);
  
  // For all view types, wrap in the PDF export component
  return (
    <div className={`responsive-report-view view-mode-${viewMode}`}>
      {/* Visible report based on device, orientation and data size */}
      <div className="block">
        <ViewWrapper>
          {renderReportView()}
        </ViewWrapper>
      </div>
      
      {/* Hidden PDF export component that can be triggered programmatically */}
      <div className="hidden" ref={pdfExportRef}>
        <ReportWithPDFExport
          data={data}
          initialOptions={{
            layout: pdfOptions.layout || PDFLayout.PORTRAIT,
            pageSize: pdfOptions.pageSize || PageSize.LETTER,
            brandingTheme: pdfOptions.brandingTheme || BrandingTheme.DEFAULT,
            includeHeader: pdfOptions.includeHeader !== undefined ? pdfOptions.includeHeader : true,
            includeFooter: pdfOptions.includeFooter !== undefined ? pdfOptions.includeFooter : true,
            companyName: pdfOptions.companyName || 'ROI Exteriors',
            documentTitle: pdfOptions.documentTitle || 'Marketing Performance Report',
            includeTimestamp: true,
            includePageNumbers: true,
            includeCompanyLogo: true,
            companyLogo: 'https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png',
            margin: 20
          }}
        />
      </div>
    </div>
  );
};

export default ResponsiveReportView;